import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Papa from 'papaparse';
import { decodeHtmlEntities } from '../utils/htmlContent';

const GCS_URL = 'https://storage.googleapis.com/staging.togeevision-f3725.appspot.com/togeeblog/content/AggregatedNews.csv';

/**
 * Async thunk to fetch and parse news articles from a CSV file.
 */
export const fetchArticles = createAsyncThunk(
  'news/fetchArticles',
  async (gcsUrl = GCS_URL, { rejectWithValue }) => {
    try {
      const response = await new Promise((resolve, reject) => {
        Papa.parse(gcsUrl, {
          download: true,
          header: true,
          skipEmptyLines: true,
          complete: (results) => resolve(results.data),
          error: (error) => reject(error),
        });
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to parse CSV data.');
    }
  }
);

const normalizeString = (str) => (str ? String(str).trim().toLowerCase() : '');

const DECODE_FIELDS = ['title', 'summary', 'description', 'content', 'excerpt', 'source', 'topic'];

const decodeArticleFields = (article) => {
  return DECODE_FIELDS.reduce((acc, field) => {
    if (Object.prototype.hasOwnProperty.call(article, field)) {
      acc[field] = decodeHtmlEntities(article[field]);
    }
    return acc;
  }, {});
};

const mapArticle = (article, index) => {
  const decodedArticle = {
    ...article,
    ...decodeArticleFields(article),
  };

  const topic = normalizeString(
    decodedArticle.topic || decodedArticle.category || decodedArticle.section || '',
  );

  return {
    ...decodedArticle,
    id:
      decodedArticle.id ||
      decodedArticle._id ||
      decodedArticle.guid ||
      decodedArticle.url ||
      `aggregated-${index}`,
    topic,
    topicLabel: topic ? topic.replace(/\b\w/g, (c) => c.toUpperCase()) : 'General',
  };
};

const initialState = {
  articles: [],
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  filters: {
    topic: 'All',
    searchTerm: '',
    sortOption: 'DateDesc',
  },
};

const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    setTopicFilter(state, action) {
      state.filters.topic = action.payload;
    },
    setSearchTerm(state, action) {
      state.filters.searchTerm = action.payload;
    },
    setSortOption(state, action) {
      state.filters.sortOption = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArticles.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.articles = (action.payload || []).map(mapArticle);
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { setTopicFilter, setSearchTerm, setSortOption } = newsSlice.actions;

export default newsSlice.reducer;
